import axios from "axios";
import { notifyAuthChanged, clearIdentity } from "./authEvents";

// En-tête portant le jeton renouvelé (miroir de `server/lib/token.js`).
// Le serveur le pose dès qu'un jeton a dépassé la moitié de sa vie ; on
// l'enregistre au passage, sans requête supplémentaire ni route dédiée.
//
// C'est ce qui évite la déconnexion du lendemain : n'importe quel appel
// authentifié prolonge la session, tant que l'utilisateur revient au moins une
// fois par quinzaine.
//
// ⚠️ Côté serveur, `REFRESH_HEADER` doit figurer dans `exposedHeaders` du CORS,
// sinon le navigateur masque l'en-tête au JavaScript de la page : le jeton
// serait bien envoyé, jamais lu, et l'utilisateur finirait quand même
// déconnecté.
const REFRESH_HEADER = "x-refresh-token";

function capture(response) {
  // axios minuscule les noms d'en-tête ; on reste tolérant par sécurité
  const fresh =
    response?.headers?.[REFRESH_HEADER] || response?.headers?.["X-Refresh-Token"];
  if (!fresh) return;
  try {
    if (localStorage.getItem("authToken") === fresh) return;
    localStorage.setItem("authToken", fresh);
    // La barre de navigation et les contextes écoutent cet événement : sans
    // lui, un onglet ouvert garderait l'ancien jeton en mémoire.
    notifyAuthChanged();
  } catch (e) {
    // Stockage indisponible (navigation privée, quota) : le jeton courant
    // reste valable, l'utilisateur ne voit rien.
  }
}

// Routes qui répondent légitimement 401 sans qu'aucune session n'expire :
// un mot de passe faux ne doit évidemment pas déconnecter l'utilisateur.
const PUBLIC_AUTH_ROUTES = ["/login", "/register", "/google", "/forgot", "/reset"];

/**
 * Ce 401 signifie-t-il « session expirée » ?
 *
 * Trois conditions, toutes nécessaires : la requête portait bien un jeton, elle
 * ne visait pas une route de connexion, et une session existe localement.
 * Sans ce filtrage, une tentative de connexion ratée effacerait l'identité de
 * l'utilisateur déjà connecté dans un autre onglet.
 */
function isExpiredSession(error) {
  if (error?.response?.status !== 401) return false;
  const cfg = error.config || {};
  const sentToken = !!(cfg.headers?.Authorization || cfg.headers?.authorization);
  if (!sentToken) return false;
  const url = String(cfg.url || "");
  if (PUBLIC_AUTH_ROUTES.some((r) => url.includes(r))) return false;
  try {
    return !!localStorage.getItem("authToken");
  } catch (e) {
    return false;
  }
}

/**
 * Installe l'intercepteur sur l'instance axios PAR DÉFAUT.
 *
 * Le web appelle `axios.get/post/...` directement depuis une vingtaine de
 * fichiers, sans instance partagée : un intercepteur global est le seul point
 * d'accroche unique, et il couvre tous les appels existants comme futurs.
 */
export function installTokenRefresh() {
  axios.interceptors.response.use(
    (response) => {
      capture(response);
      return response;
    },
    (error) => {
      // Un renouvellement peut accompagner une réponse d'erreur (403, 404…)
      if (error?.response) capture(error.response);
      if (isExpiredSession(error)) clearIdentity();
      return Promise.reject(error);
    }
  );
}
